import { type MantineTheme, Stack } from '@mantine/core';

import {
  IconArrowRight,
  IconCheck,
  IconTrophy,
  IconTrophyOff,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from './UI/Button/Button';
import { UserWithAvatar } from './UI/UserWithAvatar/UserWithAvatar';

import type { DecisionRenewalStatus } from '../entities/Interaction';
import { DecisionStatus } from '../entities/Interaction';
import type { User } from '../entities/User';

export function StaticDecisionButton({
  decisionStatus,
  owner,
}: {
  decisionStatus: DecisionStatus | DecisionRenewalStatus | undefined;
  owner?: User;
}) {
  if (!decisionStatus) {
    return null;
  }

  switch (decisionStatus) {
    case DecisionStatus.PENDING:
    case DecisionStatus.TO_ANALYZE:
    case DecisionStatus.GO:
      return (
        <StaticButton
          icon={<IconCheck size={16} />}
          decisionStatus={decisionStatus}
          owner={owner}
        />
      );

    case DecisionStatus.REJECTED:
    case DecisionStatus.NOGO:
      return (
        <StaticButton
          variant="red"
          icon={<IconX size={16} />}
          decisionStatus={decisionStatus}
          owner={owner}
        />
      );

    case DecisionStatus.ANSWERED:
      return (
        <StaticButton
          icon={<IconArrowRight size={16} />}
          decisionStatus={decisionStatus}
          owner={owner}
        />
      );
    case DecisionStatus.LOSS:
      return (
        <StaticButton
          variant="red"
          icon={<IconTrophyOff size={16} />}
          decisionStatus={decisionStatus}
          owner={owner}
        />
      );
    case DecisionStatus.WIN:
      return (
        <StaticButton
          icon={<IconTrophy size={16} />}
          decisionStatus={decisionStatus}
          owner={owner}
        />
      );
    default:
      return null;
  }
}

function StaticButton({
  icon,
  decisionStatus,
  variant = 'primary',
  owner,
}: {
  icon: React.ReactNode;
  decisionStatus: DecisionStatus | DecisionRenewalStatus;
  variant?: 'primary' | 'red';
  owner?: User;
}) {
  const { t } = useTranslation('entities');

  return (
    <Stack align="flex-end" spacing="02">
      <Button
        disabled
        variant="light"
        leftIcon={icon}
        sx={(theme: MantineTheme) => ({
          '&:disabled': {
            color:
              variant === 'primary'
                ? theme.colors.primary[7]
                : theme.colors.red[6],
            backgroundColor:
              variant === 'primary'
                ? theme.colors.primary[1]
                : theme.colors.red[1],
          },
        })}
      >
        {t(`decisionStatus.${decisionStatus}`)}
      </Button>
      {owner && (
        <UserWithAvatar
          firstName={owner.firstName}
          lastName={`${owner.lastName[0]}.`}
          avatarColor={owner.avatarColor}
          isDisabled={!!owner.disabledAt}
          w="fit-content"
          py={0}
        />
      )}
    </Stack>
  );
}
